import type {ReactElement} from 'react';
import {useContext, useEffect, useState} from 'react';
import type {Editor} from 'grapesjs';
import ClickableDiv from '@Components/clickable-div/clickable-div';
import {MODALS_IDS} from '@Components/modal-container';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import {Panel, PanelContent, PanelHeader} from '@Components/panel';
import type {PanelContentProps, PanelHeaderProps} from '@Components/panel';
import {Text, TextSize} from '@Components/text';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import {EmailMakerBottomSheet} from '@EmailMakerReact/components/email-maker-bottom-sheet';
import {VersionHistoryView} from '@EmailMakerReact/layout/email-maker-right-panel/views/version-history-view/version-history-view';
import {useAppDispatch, useAppSelector} from '@/hooks';
import styles from './email-maker-version-history-sheet.module.scss';

const PANEL_ID = 'email-maker-version-history-sheet-panel';

export function EmailMakerVersionHistorySheet(): ReactElement | null {
  const {editor} = useContext(emailMakerContext);
  const dispatch = useAppDispatch();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const isOpen = useAppSelector((s) => s.modals.modalsHashmap[MODALS_IDS.EMAIL_MAKER_VERSION_HISTORY_SHEET]?.isOpen ?? false);

  useEffect(() => {
    if (!isOpen) {
      setIsCollapsed(false);
    }
  }, [isOpen]);

  if (!isOpen || !editor) {
    return null;
  }

  function handleClose(): void {
    dispatch(closeModal(MODALS_IDS.EMAIL_MAKER_VERSION_HISTORY_SHEET));
  }

  function toggleCollapsed(): void {
    setIsCollapsed((collapsed) => !collapsed);
  }

  function renderCollapsibleTitle(): ReactElement {
    return (
      <ClickableDiv onClick={toggleCollapsed} className={styles.collapseToggle} aria-label={window.i18next.t('pmwjs_version_history')} aria-expanded={!isCollapsed}>
        <Text val={window.i18next.t('pmwjs_version_history')} size={TextSize.SMALL} bold />
      </ClickableDiv>
    );
  }

  function renderPanelHeader(): ReactElement<PanelHeaderProps> {
    return (
      <PanelHeader
        customTitle={renderCollapsibleTitle()}
        onClose={handleClose}
        className={styles.sheetHeader}
        actionsClassName={styles.sheetHeaderActions}
        closeIconClasses={styles.sheetHeaderClose}
      />
    );
  }

  function renderPanelContent(activeEditor: Editor): ReactElement<PanelContentProps> {
    return (
      <PanelContent className={`${styles.sheetContent} ${isCollapsed ? styles.collapsed : ''}`}>
        <VersionHistoryView editor={activeEditor} onClose={handleClose} hideHeader />
      </PanelContent>
    );
  }

  return (
    <EmailMakerBottomSheet modalId={MODALS_IDS.EMAIL_MAKER_VERSION_HISTORY_SHEET} panelId={PANEL_ID}>
      <Panel panelHeader={renderPanelHeader()} panelContent={renderPanelContent(editor)} />
    </EmailMakerBottomSheet>
  );
}
